import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown, Settings, Calendar, Trash2, Radio, UserPlus } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import NameBlock from './NameBlock';
import MatchScoreCell, { parseSets } from './MatchScoreCell';
import MatchSettingsModal from '../MatchSettingsModal';
import AddEventModal from '../../../events/components/AddEventModal';
import AssignParticipantsModal from '../AssignParticipantsModal';
import ConfirmModal from '../../../components/ConfirmModal';
import StartScoringConfirmModal from '../StartScoringConfirmModal';
import { isEnabled, isScorer, TOURNAMENT_ENTITIES } from '../../../tournaments/access';

// Structural borders/dividers/hover overlays are ported as literal hardcoded
// Tailwind dark: classes, not theme.* tokens — this matches production's
// own ScheduleTab.jsx exactly (see :1666-1940), which uses plain neutral
// grays regardless of which of this app's 10 accent palettes is active.
export const DIVIDER = 'border-gray-300 dark:border-gray-600';
export const ROW_HOVER = 'hover:bg-black/5 dark:hover:bg-white/5';

// Same reasoning as DIVIDER/ROW_HOVER above — a status pill's color is a
// semantic signal (mirrors production's own GameCard.jsx STATUS_BADGES map
// exactly), not an accent-palette choice, so it's hardcoded rather than
// pulled from theme.*. 'draft' isn't a real checkin_status value — it's
// this module's own placeholder for "not saved to the backend yet" (see
// scheduleRows.js's resolveOverrides), kept visually quiet/neutral like the
// single "Draft" badge this row used to always show.
export const STATUS_BADGES = {
  draft: { label: 'Draft', className: 'bg-gray-400/20 text-gray-500 dark:text-gray-400' },
  scheduled: { label: 'Scheduled', className: 'bg-gray-500 text-white' },
  checking_in: { label: 'Checking In', className: 'bg-yellow-500 text-black' },
  ready: { label: 'Ready', className: 'bg-green-500 text-white' },
  live: { label: 'Live', className: 'bg-red-500 text-white animate-pulse' },
  completed: { label: 'Completed', className: 'bg-blue-500 text-white' },
};

// Once the tournament itself is `in_progress`, organizers/scorers/co-owners
// see this instead of the row's plain status badge (unless the match is
// already live/completed, which is informative enough on its own) — a
// nudge that THIS row is actionable, not just a status readout. Viewers
// keep seeing the real STATUS_BADGES value; see MatchRow's `showStartScoringPill`.
const START_SCORING_PILL = { label: 'Start Scoring', className: 'bg-blue-500 text-white' };

// A direct port of ScheduleTab.jsx's handleEditMatch pre-fill logic, scoped
// to what this row actually has (`liveData` — only ever built from a real
// saved match, see scheduleApi.js's buildLiveData doc comment). No tournament-
// level defaults (facility address, tournament start date) here — this
// module doesn't thread the full `tournament` object down this far, so an
// unscheduled match just starts from today/09:00-10:00 instead.
const buildEventInitialData = (liveData) => {
  const event = liveData.event;
  const fallbackSubject = `${liveData.participant_a_name || 'TBD'} vs ${liveData.participant_b_name || 'TBD'}`;
  if (event?.start_time) {
    const startDate = new Date(event.start_time);
    const endDate = event.end_time ? new Date(event.end_time) : null;
    return {
      subject: event.subject || fallbackSubject,
      address: event.venue?.fullAddress || '',
      locationName: event.venue?.name || '',
      date: startDate.toISOString().split('T')[0],
      startTime: startDate.toTimeString().slice(0, 5),
      endTime: endDate ? endDate.toTimeString().slice(0, 5) : '10:00',
    };
  }
  const now = new Date();
  return {
    subject: fallbackSubject,
    address: '',
    date: now.toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '10:00',
  };
};

// One game row — the thing that repeats most on this page, so it's the one
// component reused verbatim by both responsive layouts rather than having
// each layout hand-roll its own row markup. `variant` picks which DOM shape
// production actually uses for that breakpoint (mobile: a card `<div>`,
// desktop: a table `<tr>`) — the row's data and NameBlock rendering are
// otherwise identical between the two.
//
// Status reads "Draft" until this schedule has actually been saved to the
// backend (see scheduleRows.js's resolveOverrides) — from then on it's the
// real checkin_status (Scheduled/Checking In/Ready/Live/Completed), same
// states production's own ScheduleTab.jsx shows. Clicking the row opens
// MatchDetailModal, which is where check-in/Start Scoring actually happen
// (see that file) — this row stays a read-mostly summary. The up/down Order
// buttons ARE real — see MatchTable's `moveRow`. The gear
// icon opens this ROW's own Match Settings modal (court override, custom
// match rules, Team Pairing) — see MatchSettingsModal.jsx. Clicking anywhere
// else on the row opens MatchDetailModal (see MatchTable.jsx, which owns
// that state) — `onOpenDetail` is undefined wherever a caller doesn't want
// that (there's currently no such caller, but the prop stays optional to
// match every other callback here). The order/gear buttons stopPropagation
// so clicking THEM doesn't also open the detail view underneath.
const MatchRow = ({
  row,
  roundLabel,
  variant,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  showCourt,
  courts,
  sportType,
  tournamentMatchSettings,
  onApplyRowOverride,
  onOpenDetail,
  tournamentVO,
  currentUserId,
  canManageThisSchedule,
  updateMatchSchedule,
  deleteMatchRow,
  assignMatchParticipants,
  fetchAllParticipants,
  participantType,
  startScoring,
  openLiveGame,
}) => {
  const { theme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [isStartingScore, setIsStartingScore] = useState(false);
  const [scoreExpanded, setScoreExpanded] = useState(false);
  const [isOpeningLiveGame, setIsOpeningLiveGame] = useState(false);
  // The round header directly above already states the round once (and, for
  // formats like Dual Meet/Series, `roundLabel` IS the "Team A vs Team B"
  // matchup name — identical to what Home/Away already show) — so a row only
  // needs its OWN identifying text when it's one discipline of a larger tie
  // (gameTypeLabel, e.g. "Men's Doubles"). A plain single-match row has
  // nothing else to add and falls back to the round label. `groupLabel` is
  // separate and additive — Group Stage + Knockout tags each pool-play row
  // with which single group it belongs to (e.g. "Group A") since a merged
  // "Group Stage — Round 2" section interleaves every group's games and
  // nothing else on the row reveals which pool a given match is from. (Cross-
  // Group's own "A vs B" tie label is a different thing — see
  // crossGroupGenerator.js — and suppresses itself down to `undefined`
  // whenever it would just be the same trivial string on every row.)
  const rowLabel = row.gameTypeLabel || roundLabel;
  const meta = (
    <>
      #{row.number} · {rowLabel}
      {row.groupLabel && ` · ${row.groupLabel}`}
    </>
  );

  // Literal port of ScheduleTab.jsx's renderStatusBadge ternary (:1594-1612):
  // Live/Completed always win first; otherwise the assigned SCORER
  // specifically (not just any organizer/co-owner — production scopes this
  // to isScorer only) sees a "Start Scoring" pill, blue+clickable once the
  // tournament has actually started, grey+disabled before that — never
  // gated on check-in status the way GAME_START's access.js rule is,
  // because production's own row-level badge isn't either. Everyone else
  // (viewers, organizers who aren't the scorer) keeps seeing the plain
  // real status.
  const isThisUserTheScorer = isScorer(currentUserId, tournamentVO);
  const tournamentStarted = tournamentVO?.status === 'in_progress';
  const showStartScoringPill = isThisUserTheScorer && row.status !== 'live' && row.status !== 'completed';
  const canStartScoring = showStartScoringPill && tournamentStarted && !!row.liveData && !!startScoring;
  const statusInfo = showStartScoringPill ? START_SCORING_PILL : STATUS_BADGES[row.status] || STATUS_BADGES.draft;
  const statusBadgeClassName = `inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusInfo.className}`;
  const statusBadge = canStartScoring ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setStartConfirmOpen(true);
      }}
      className={statusBadgeClassName}
    >
      <Radio size={9} />
      {statusInfo.label}
    </button>
  ) : (
    <span className={`${statusBadgeClassName} ${showStartScoringPill ? 'cursor-not-allowed opacity-60' : ''}`}>
      {showStartScoringPill && <Radio size={9} />}
      {statusInfo.label}
    </span>
  );

  // Live/completed matches skip Match Details entirely and go straight to
  // the game component, same as production's row badge (its Live button
  // calls `handleOpenLiveTab` directly, no modal in between) — matching the
  // explicit ask that clicking a scorable/live/completed row shouldn't add
  // an extra "Match Details" layer before reaching the actual score entry.
  // No role restriction, matching production: the Live badge is clickable
  // for anyone, not just the scorer (read-only for non-scorers happens
  // inside the game component itself, not gated here).
  const canOpenLiveGame = (row.status === 'live' || row.status === 'completed') && !!row.liveData && !!openLiveGame;

  // Not-started matches deliberately do NOT open the confirm from anywhere
  // on the row — only the Start Scoring badge itself (its own onClick above
  // already stopPropagation's) does that. Clicking elsewhere on the row goes
  // to Match Details/fullscreen detail view instead, same as any other
  // not-yet-live row, so browsing the schedule doesn't risk claiming scorer
  // by accident. Live/completed rows are unchanged — the whole row still
  // opens the game directly, matching production's own Live button.
  const handleRowClick = () => {
    if (canOpenLiveGame) {
      if (isOpeningLiveGame) return;
      setIsOpeningLiveGame(true);
      Promise.resolve(openLiveGame(row)).finally(() => setIsOpeningLiveGame(false));
      return;
    }
    onOpenDetail?.();
  };

  const canSchedule = !!row.liveData && isEnabled(currentUserId, TOURNAMENT_ENTITIES.MATCH_SCHEDULE, tournamentVO, { game: row.liveData });
  const canDelete = !!row.liveData && isEnabled(currentUserId, TOURNAMENT_ENTITIES.MATCH_DELETE, tournamentVO, { game: row.liveData });

  // `createRound` always creates one placeholder match with no participants,
  // for every round type (not just knockout/playoff) — see
  // useScheduleBuilder's `assignMatchParticipants` doc comment. Without this,
  // NameBlock's "both entry and placeholder empty" case renders "BYE" on
  // both sides with no way to fix it, which reads as a real bye instead of
  // "not assigned yet".
  const needsParticipants = !!row.liveData && !row.liveData.participant_a_id && !row.liveData.participant_b_id;
  const canAssign = needsParticipants && !!assignMatchParticipants &&
    isEnabled(currentUserId, TOURNAMENT_ENTITIES.MATCH_EDIT, tournamentVO, { game: row.liveData });

  const scheduleButton = canSchedule && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setScheduleModalOpen(true);
      }}
      aria-label="Edit schedule"
      className={`rounded p-0.5 transition-colors ${theme.textMuted} ${ROW_HOVER}`}
      title={row.liveData.event?.start_time ? 'Edit schedule' : 'Schedule match'}
    >
      <Calendar size={14} />
    </button>
  );

  const deleteButton = canDelete && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setDeleteConfirmOpen(true);
      }}
      disabled={isDeleting}
      aria-label="Delete match"
      className={`rounded p-0.5 transition-colors ${theme.textMuted} ${ROW_HOVER} disabled:opacity-40`}
      title="Delete match"
    >
      <Trash2 size={14} />
    </button>
  );

  // Site-standard destructive-action confirm (src/components/ConfirmModal.jsx
  // — the pattern used for deletes across teams/squads/tournaments), not the
  // native window.confirm() this row used before. Wrapped in a portal +
  // stopPropagation div for the same reason as scheduleModal above:
  // ConfirmModal itself isn't portaled and only stops propagation on its own
  // inner card, not the outer backdrop click, so without this wrapper a
  // backdrop click here would also bubble up and fire this row's onClick.
  const deleteConfirmModal = canDelete && createPortal(
    <div onClick={(e) => e.stopPropagation()}>
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await deleteMatchRow(row);
            setDeleteConfirmOpen(false);
          } finally {
            setIsDeleting(false);
          }
        }}
        title="Delete Match"
        message="Are you sure you want to delete this match? This action cannot be undone."
        confirmText="Delete"
        confirmStyle="danger"
        loading={isDeleting}
      />
    </div>,
    document.body
  );

  // Shared confirm — see StartScoringConfirmModal.jsx doc comment. Triggered
  // directly from the row's badge (see `canStartScoring` above), not nested
  // inside a bigger Match Details panel the way this module's own
  // MatchDetailModal.jsx does it for a click elsewhere on the row.
  const startScoringConfirmModal = canStartScoring && (
    <StartScoringConfirmModal
      isOpen={startConfirmOpen}
      onClose={() => setStartConfirmOpen(false)}
      row={row}
      isStarting={isStartingScore}
      onConfirm={async () => {
        setIsStartingScore(true);
        try {
          await startScoring(row);
          setStartConfirmOpen(false);
        } finally {
          setIsStartingScore(false);
        }
      }}
    />
  );

  // AddEventModal is shared production code (also used by the old,
  // frozen ScheduleTab.jsx) — it isn't portaled and doesn't stopPropagation
  // on its own root, so it's wrapped here rather than edited directly. Same
  // two reasons as MatchSettingsModal.jsx: portal escapes GeneratedScheduleStep's
  // `tf-animate-in` wrapper (whose transform-touching animation traps
  // `position: fixed` descendants after it finishes, breaking centering),
  // and the wrapper's `stopPropagation` is separately required since a
  // portal only relocates DOM placement, not the REACT tree synthetic
  // events bubble through — this row's own onClick opens Match Details.
  const scheduleModal = canSchedule && createPortal(
    <div onClick={(e) => e.stopPropagation()}>
      <AddEventModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        initialData={buildEventInitialData(row.liveData)}
        onSave={async (eventData) => {
          await updateMatchSchedule(row, eventData);
          setScheduleModalOpen(false);
        }}
      />
    </div>,
    document.body
  );

  const assignModal = canAssign && (
    <AssignParticipantsModal
      isOpen={assignModalOpen}
      onClose={() => setAssignModalOpen(false)}
      row={row}
      participantType={participantType}
      fetchAllParticipants={fetchAllParticipants}
      onSave={assignMatchParticipants}
    />
  );

  const orderButtons = (onMoveUp || onMoveDown) && (
    <div className="flex shrink-0 items-center gap-0.5">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMoveUp();
        }}
        disabled={!canMoveUp}
        aria-label="Move up"
        className={`rounded p-0.5 transition-colors ${canMoveUp ? `${theme.textMuted} ${ROW_HOVER}` : 'cursor-not-allowed opacity-30'}`}
      >
        <ChevronUp size={14} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMoveDown();
        }}
        disabled={!canMoveDown}
        aria-label="Move down"
        className={`rounded p-0.5 transition-colors ${canMoveDown ? `${theme.textMuted} ${ROW_HOVER}` : 'cursor-not-allowed opacity-30'}`}
      >
        <ChevronDown size={14} />
      </button>
    </div>
  );

  const gearButton = onApplyRowOverride && (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setSettingsOpen(true);
      }}
      aria-label="Match settings"
      // row.useCustomSettings (not hasRowOverride) — the fully resolved flag
      // already accounts for an active ROUND override via local roundOverrides
      // state, available synchronously the instant Apply is clicked, unlike
      // hasRowOverride which only turns true once this row's own liveData.
      // overridden_match_settings comes back from the backend refresh that
      // follows a round-level apply. Using hasRowOverride here meant this
      // icon (and the modal it opens) looked un-customized for a moment
      // right after applying round settings, showing stale defaults instead
      // of what's actually about to be in effect for this match.
      className={`rounded p-0.5 transition-colors ${theme.textMuted} ${ROW_HOVER} ${row.useCustomSettings ? theme.text : ''}`}
      title={row.useCustomSettings ? 'Match settings (customized)' : 'Match settings'}
    >
      <Settings size={14} fill={row.useCustomSettings ? 'currentColor' : 'none'} />
    </button>
  );

  const settingsModal = onApplyRowOverride && (
    <MatchSettingsModal
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      scope="match"
      sportType={sportType}
      courts={courts}
      tournamentMatchSettings={tournamentMatchSettings}
      initialOverride={row.useCustomSettings ? row : null}
      slotA={row.slotA}
      slotB={row.slotB}
      onApply={(override) => onApplyRowOverride(row.key, override, row.liveData)}
    />
  );

  const courtBadge = showCourt && (
    <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${theme.backgroundSecondary} ${theme.textMuted}`}>
      {row.court || '—'}
    </span>
  );

  // Mobile's 3-column grid has no room for MatchScoreCell's own expand list
  // (see MatchScoreCell.jsx) without squeezing the middle column, so the
  // compact score/vs indicator is inlined here and the expanded per-set list
  // renders as a full-width block below the grid instead — same parseSets
  // logic, different layout.
  const mobileSets = row.status === 'completed' ? parseSets(row.liveData) : [];

  // Replaces the normal Home/vs/Away display for a placeholder match with
  // no participants yet — showing NameBlock as-is would render "BYE" on
  // both sides (see NameBlock.jsx's `isEmpty` case), which reads as a real
  // bye rather than "nobody's been assigned here yet".
  const matchupBlock = needsParticipants ? (
    canAssign ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAssignModalOpen(true);
        }}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-sm font-semibold transition-colors ${theme.border} ${theme.textMuted} ${ROW_HOVER}`}
      >
        <UserPlus size={14} />
        Assign Teams
      </button>
    ) : (
      <div className={`text-center text-sm italic ${theme.textMuted}`}>Teams not yet assigned</div>
    )
  ) : (
    <div>
      <div className="grid grid-cols-3 items-center gap-1">
        <NameBlock entry={row.slotA} placeholder={row.placeholderA} players={row.playersA} align="text-right" />
        {mobileSets.length > 1 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setScoreExpanded((v) => !v);
            }}
            className={`flex items-center justify-center gap-1 text-sm font-bold ${theme.text}`}
          >
            {mobileSets.filter((s) => s.a > s.b).length} – {mobileSets.filter((s) => s.b > s.a).length}
            {scoreExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        ) : mobileSets.length === 1 ? (
          <span className={`text-center text-sm font-bold ${theme.text}`}>{mobileSets[0].a} – {mobileSets[0].b}</span>
        ) : (
          <span className={`text-center text-xs font-semibold ${theme.textMuted}`}>vs</span>
        )}
        <NameBlock entry={row.slotB} placeholder={row.placeholderB} players={row.playersB} align="text-left" />
      </div>
      {scoreExpanded && mobileSets.length > 1 && (
        <div className={`mt-1 space-y-0.5 text-center text-xs ${theme.textMuted}`} onClick={(e) => e.stopPropagation()}>
          {mobileSets.map((set, i) => (
            <div key={i}>Set {i + 1}: {set.a} – {set.b}</div>
          ))}
        </div>
      )}
    </div>
  );

  if (variant === 'mobile') {
    return (
      <div
        className={`px-3 py-3 ${(onOpenDetail || canStartScoring || canOpenLiveGame) ? `cursor-pointer ${ROW_HOVER}` : ''}`}
        onClick={handleRowClick}
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className={`min-w-0 flex-1 truncate text-xs ${theme.textMuted}`}>{meta}</span>
          {/* flex-wrap so a long round label + Court/Start-Scoring/Order/
              Calendar/Settings/Delete icons don't get clipped off the edge
              on narrow phones (previously the gear icon in particular was
              being pushed out of view and unclickable) — wraps to its own
              line instead of overflowing. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {courtBadge}
            {statusBadge}
            {orderButtons}
            {scheduleButton}
            {gearButton}
            {deleteButton}
          </div>
        </div>
        {matchupBlock}
        {settingsModal}
        {scheduleModal}
        {assignModal}
        {deleteConfirmModal}
        {startScoringConfirmModal}
      </div>
    );
  }

  return (
    <tr
      className={`border-b ${DIVIDER} transition-colors last:border-0 ${ROW_HOVER} ${(onOpenDetail || canStartScoring || canOpenLiveGame) ? 'cursor-pointer' : ''}`}
      onClick={handleRowClick}
    >
      <td className={`border-r px-3 py-2 text-center text-xs ${theme.textMuted} ${DIVIDER}`}>{row.number}</td>
      <td className={`border-r px-3 py-2 text-xs ${theme.textMuted} ${DIVIDER}`}>
        {rowLabel}
        {row.groupLabel && <div className="opacity-75">{row.groupLabel}</div>}
      </td>
      {needsParticipants ? (
        <td className={`border-r px-3 py-2 ${DIVIDER}`} colSpan={3}>
          {matchupBlock}
        </td>
      ) : (
        <>
          <td className={`border-r px-3 py-2 ${DIVIDER}`}>
            <NameBlock entry={row.slotA} placeholder={row.placeholderA} players={row.playersA} />
          </td>
          <td className={`border-r px-3 py-2 text-center ${DIVIDER}`}>
            <MatchScoreCell row={row} />
          </td>
          <td className={`border-r px-3 py-2 ${DIVIDER}`}>
            <NameBlock entry={row.slotB} placeholder={row.placeholderB} players={row.playersB} />
          </td>
        </>
      )}
      {showCourt && (
        <td className={`border-r px-3 py-2 text-center ${DIVIDER}`}>{courtBadge}</td>
      )}
      <td className={`border-r px-3 py-2 text-center ${DIVIDER}`}>{statusBadge}</td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-center gap-1">
          {orderButtons}
          {scheduleButton}
          {gearButton}
          {deleteButton}
        </div>
      </td>
      {settingsModal}
      {scheduleModal}
      {assignModal}
      {deleteConfirmModal}
      {startScoringConfirmModal}
    </tr>
  );
};

export default MatchRow;
